import { setLoginContext } from 'app/utils/auth';
import { logToSentry } from 'core/utils/sentry';

/**
 * Determines the redirect path when the user logs in with a token.
 *
 * Handles three scenarios:
 * 1. Deep link paths (path segments > 4) - extracts and returns the deep link path
 * 2. Valid redirect_uri query parameter - redirects via window.location.href
 * 3. No redirect_uri / fallback - returns '/home'
 *
 * Also validates redirect_uri to prevent open redirect vulnerabilities.
 * Invalid redirect_uri values are logged to Sentry and redirect to '/not-found'.
 *
 * @param {object} options
 * @param {Function} options.dispatch - Redux dispatch function
 * @param {object} options.coreEntities - Core entities with auth actions
 * @returns {string} The path to redirect to
 */
export function redirectFromLoginWithToken({ dispatch, coreEntities }) {
  const path = window.location.pathname;
  const queryParams = window.location.search;
  const urlParams = new URLSearchParams(queryParams);
  const redirectUri = urlParams.get('redirect_uri');

  if (path.startsWith('/saml/callback/')) {
    setLoginContext(queryParams);
  }

  if (path.startsWith('/auth/login/') || path.includes('/saml/callback/')) {
    const splitted = path.split('/');
    if (splitted.length > 4) {
      const newPath = '/' + splitted[splitted.length - 1].replaceAll('&', '/');

      dispatch(coreEntities.auth.actions.requestUser());

      return queryParams ? `${newPath}${queryParams}` : newPath;
    }
  }

  if (redirectUri) {
    try {
      new URL(redirectUri, window.location.origin);
      window.location.href = redirectUri;
      return '/home';
    } catch {
      logToSentry('warning', 'Invalid redirect_uri in login', {
        tags: { page: 'login_redirect' },
        extra: { redirectUri, currentPath: path },
      });
      return '/not-found';
    }
  } else {
    return '/home';
  }
}
